import type { MessageSubType, MessageType, SmartOfferActions } from "@expert/shared-types";

import type { DefaultMessageKey } from "../../shared-types";
import type { MultipleActions, QuickAction } from "./types";

import { useTimelineStore } from "../../state";
import { getQuickActions } from "./getQuickActions";
import { useCreateActions } from "./useCreateActions";

interface QuickActionsManagerProps {
    currentMessageType: MessageType;
    messageId?: string;
    messageSubType?: MessageSubType;
    defaultMessageKey?: DefaultMessageKey;
    isOverflowing: boolean;
    isExpanded: boolean;
    isLastBotMessage: boolean;
    isLastProactiveMessage: boolean;
    setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    smartOfferActions?: SmartOfferActions;
    isSaleSuccessOrDeclined?: boolean;
}

/*
Based on design, only a single quick actions group is showing on the screen at any time.
*/
export function useQuickActionsManager({
    currentMessageType,
    messageId,
    messageSubType,
    isOverflowing,
    isExpanded,
    isLastBotMessage,
    isLastProactiveMessage,
    setIsExpanded,
    smartOfferActions,
    isSaleSuccessOrDeclined,
}: QuickActionsManagerProps): QuickAction[] {
    const { currentSessionId, getTimelineState } = useTimelineStore();
    const { partner } = getTimelineState(currentSessionId);

    const allActions = useCreateActions({
        setIsExpanded,
        messageId,
        messageType: currentMessageType,
        smartOfferActions,
    });
    const currentActions: MultipleActions[] = [];

    // Allow SmartOffer to show actions even if not last message
    const shouldShowActions = isLastBotMessage || isLastProactiveMessage || currentMessageType === "SmartOffer";

    if (shouldShowActions) {
        switch (currentMessageType) {
            case "Search": {
                if (partner === "att") {
                    currentActions.push(allActions.neutralOrPositiveFeedback, allActions.tsnr);
                }
                break;
            }
            case "Proactive": {
                if (isOverflowing && !isExpanded) {
                    currentActions.push(allActions.expandMessage, allActions.negativeFeedback);
                } else {
                    currentActions.push(allActions.neutralOrPositiveFeedback, allActions.negativeFeedback);
                }
                break;
            }
            case "SessionSummary":
                if (isOverflowing && !isExpanded) {
                    currentActions.push(allActions.expandMessage);
                }
                break;
            case "SmartOffer": {
                switch (messageSubType) {
                    case "offer":
                        if (!smartOfferActions?.isSmartOfferActionUsed?.("sendOffer")) {
                            currentActions.push(allActions.sendToCustomer);
                        }
                        break;
                    case "enrollment": {
                        if (
                            !isSaleSuccessOrDeclined &&
                            !smartOfferActions?.isSmartOfferActionUsed?.("sendEnrollment") &&
                            !smartOfferActions?.isSmartOfferActionUsed?.("decline")
                        ) {
                            currentActions.push(allActions.sendEnrollment, allActions.declined);
                        }
                        break;
                    }
                    case "waitingEnrollment": {
                        if (!isSaleSuccessOrDeclined && !smartOfferActions?.isSmartOfferActionUsed?.("decline")) {
                            // Show resend link if max attempts NOT reached (don't check if action was used)
                            if (!smartOfferActions?.isMaxAttemptsReached) {
                                currentActions.push(allActions.resendDisclosureLink);
                            }
                            currentActions.push(allActions.declined, allActions.previewDisclosures);
                        }
                        break;
                    }
                    case "celebratory":
                        if (!smartOfferActions?.isSmartOfferActionUsed?.("sendCongratulations")) {
                            currentActions.push(allActions.sendToCustomer);
                        }
                        break;
                }
                break;
            }
            default:
                break;
        }
    }

    const quickActions = getQuickActions(currentActions);
    return quickActions;
}
